import React, {useEffect, useMemo, useRef, useState} from 'react'
import {actions as authActions} from '../../redux/modules/auth'
import {actions as userActions} from '../../redux/modules/users'
import {useDispatch, useSelector} from 'react-redux'
import {Avatar, Box, Button, Container, TextField, Typography} from '@mui/material'
import {validate} from 'isemail'
import styles from './styles'
import {RootState} from 'redux/rootReducer'
import AlertMessage from '../../components/AlertMessage'

const LoginScreen: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
  const [forgotPasswordFormError, setForgotPasswordFormError] = useState<string | undefined>()
  const [sendingForgotPassword, setSendingForgotPassword] = useState(false)
  const dispatch = useDispatch()
  const loginError = useSelector(({auth}: RootState) => auth.error)
  const forgotPasswordMessage = useSelector(({users}: RootState) => users.forgotPasswordMessage)
  const usersLoading = useSelector(({users}: RootState) => users.loading)
  // Same double-submit guard as resetPassword/index.tsx: a ref is checked/set
  // synchronously so a fast double-click or Enter+click can't fire twice
  // before the first re-render lands.
  const sendingForgotPasswordRef = useRef(false)

  const errorMessage = useMemo(() => {
    if (loginError) {
      return loginError
    }

    return undefined
  }, [loginError])

  useEffect(() => {
    if (!sendingForgotPassword || usersLoading) {
      return
    }
    sendingForgotPasswordRef.current = false
    setSendingForgotPassword(false)
  }, [usersLoading])

  const login = () => {
    dispatch(authActions.login({email, password}))
  }

  const openForgotPassword = () => {
    setForgotPasswordFormError(undefined)
    dispatch(userActions.setForgotPasswordMessage({message: undefined}))
    setShowForgotPassword(true)
  }

  const closeForgotPassword = () => {
    dispatch(userActions.setForgotPasswordMessage({message: undefined}))
    setShowForgotPassword(false)
  }

  const sendForgotPasswordInstructions = () => {
    if (sendingForgotPasswordRef.current) {
      return
    }
    setForgotPasswordFormError(undefined)
    const validEmail = validate(forgotPasswordEmail, {minDomainAtoms: 2})
    if (!validEmail) {
      setForgotPasswordFormError('Valid email address required.')
      return
    }
    sendingForgotPasswordRef.current = true
    setSendingForgotPassword(true)
    dispatch(userActions.sendResetPasswordInstructionsByEmail({email: forgotPasswordEmail}))
  }

  if (showForgotPassword) {
    return (
      <Container component="main" sx={styles.root}>
        <Avatar sx={styles.logo} variant="square" src="logo.png" />
        <Box sx={styles.form} textAlign="center">
          <Typography sx={styles.headerLabel} variant="h6">
            Reset your password
          </Typography>
          <AlertMessage
            error={forgotPasswordFormError}
            severity="error"
            sx={{marginBottom: 2, width: 300}}
          />
          <AlertMessage
            error={forgotPasswordMessage}
            severity="success"
            sx={{marginBottom: 2, width: 300}}
          />
          <TextField
            sx={styles.textField}
            value={forgotPasswordEmail}
            onChange={e => setForgotPasswordEmail(e.target.value)}
            placeholder="Email"
            onKeyDown={args => args.key === 'Enter' && sendForgotPasswordInstructions()}
          />
          <Button
            variant="contained"
            sx={styles.loginButton}
            onClick={sendForgotPasswordInstructions}
            disabled={sendingForgotPassword}>
            Send reset instructions
          </Button>
          <Button variant="text" sx={styles.forgotPasswordLink} onClick={closeForgotPassword}>
            Back to login
          </Button>
        </Box>
      </Container>
    )
  }

  return (
    <Container component="main" sx={styles.root}>
      <Avatar sx={styles.logo} variant="square" src="logo.png" />
      <Box sx={styles.form} textAlign="center">
        <AlertMessage error={errorMessage} severity="error" sx={{marginBottom: 2, width: 300}} />

        <TextField
          sx={styles.textField}
          onChange={e => setEmail(e.target.value)}
          placeholder="Email"
          onKeyDown={args => args.key === 'Enter' && login()}
        />
        <TextField
          sx={styles.textField}
          type="password"
          onChange={e => setPassword(e.target.value)}
          placeholder="Password"
          onKeyDown={args => args.key === 'Enter' && login()}
        />
        <Button variant="contained" sx={styles.loginButton} onClick={login}>
          Login
        </Button>
        <Button variant="text" sx={styles.forgotPasswordLink} onClick={openForgotPassword}>
          Forgot password?
        </Button>
      </Box>
    </Container>
  )
}
export default LoginScreen
